import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CheckCircle2, Eye, Megaphone, ScanLine, ShieldAlert, ClipboardCheck } from "lucide-react";

const stages = [
  { k: "Detect", icon: ScanLine, d: "AI-assisted analysis flags unusual activity in a camera view." },
  { k: "Verify", icon: Eye, d: "A trained human reviewer checks the clip before anything is escalated." },
  { k: "Escalate", icon: Megaphone, d: "Only authorised contacts, agreed in advance, receive a clear alert." },
  { k: "Resolve", icon: ClipboardCheck, d: "Actions and outcomes are logged against the event for review." },
];

const cams = [
  { id: "cam-01", label: "Gate", src: "/images/cctv.jpg" },
  { id: "cam-02", label: "Site yard", src: "/images/construction-nairobi.jpg" },
  { id: "cam-03", label: "Comms room", src: "/images/patch-panel.jpg" },
  { id: "cam-04", label: "Solar array", src: "/images/solar.jpg" },
];

const events = [
  ["22:41", "Motion after hours · Gate camera", "review"],
  ["22:42", "Reviewer confirmed: delivery vehicle", "cleared"],
  ["01:15", "Person near perimeter · Yard camera", "review"],
  ["01:16", "Authorised site contact alerted", "escalated"],
];

const tone = { review: "text-amber-300 border-amber-400/30 bg-amber-400/10", cleared: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10", escalated: "text-orange-300 border-orange-400/30 bg-orange-400/10" };

const marks = [
  { x: 31, t: "22:41", l: "Motion · Gate", s: "review" },
  { x: 33, t: "22:42", l: "Cleared: delivery", s: "cleared" },
  { x: 60, t: "01:15", l: "Person · Yard", s: "review" },
  { x: 62, t: "01:16", l: "Contact alerted", s: "escalated" },
  { x: 84, t: "04:05", l: "Cleared: animal", s: "cleared" },
];
const signal = [12, 18, 10, 22, 15, 30, 26, 14, 20, 76, 88, 40, 18, 12, 16, 24, 20, 14, 82, 91, 64, 22, 16, 12, 28, 72, 30, 14];

export const BrendanConsole = () => {
  const [step, setStep] = useState(0);
  const [cam, setCam] = useState(0);
  const reduce = useReducedMotion();

  useEffect(() => {
    if (reduce) return undefined;
    const t = setInterval(() => setStep((s) => (s + 1) % stages.length), 2400);
    return () => clearInterval(t);
  }, [reduce]);

  return (
    <div className="relative overflow-hidden rounded-[28px] border border-white/10 bg-[#0F0D0C] p-3 shadow-[0_40px_120px_-40px_rgba(234,88,12,0.35)] sm:p-4" data-testid="brendan-console">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-black/40 px-4 py-3">
        <div className="flex items-center gap-3">
          <ShieldAlert className="h-4 w-4 text-orange-400" />
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-stone-300">Brendan · Concept interface</span>
        </div>
        <span className="flex flex-wrap gap-2">
          <span className="rounded-full border border-orange-400/40 bg-orange-500/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-orange-200">In development</span>
          <span className="rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-amber-200" data-testid="brendan-concept-badge">
            Conceptual demo · Simulated data
          </span>
        </span>
      </div>

      <div className="mt-3 grid gap-3 lg:grid-cols-5">
        <div className="flex min-w-0 flex-col gap-3 lg:col-span-3">
        <div className="relative aspect-video overflow-hidden rounded-2xl bg-stone-900 lg:aspect-auto lg:min-h-[340px] lg:flex-1">
          {cams.map((c, i) => (
            <img key={c.id} src={c.src} alt={i === cam ? `Sample image illustrating the ${c.label} camera concept` : ""} loading="lazy"
              className={`absolute inset-0 h-full w-full object-cover grayscale transition-opacity duration-700 ${i === cam ? "opacity-60" : "opacity-0"}`} />
          ))}
          <div className="tc-scanlines absolute inset-0 opacity-40" />
          <motion.div className="absolute rounded-md border-2 border-orange-500 shadow-[0_0_24px_rgba(249,115,22,0.5)]"
            animate={reduce ? {} : { left: ["18%", "46%", "30%", "18%"], top: ["22%", "34%", "48%", "22%"] }}
            transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
            style={{ left: "18%", top: "22%", width: "24%", height: "34%" }}>
            <span className="absolute -top-6 left-0 whitespace-nowrap rounded bg-orange-500 px-1.5 py-0.5 font-mono text-[10px] text-black">event · needs review</span>
          </motion.div>
          <div className="absolute bottom-3 left-3 right-3 flex justify-between font-mono text-[10px] uppercase tracking-[0.18em] text-stone-200">
            <span className="flex shrink-0 items-center gap-2"><span className="tc-pulse h-1.5 w-1.5 rounded-full bg-red-500" /> Sample feed</span>
            <span className="truncate pl-3">{cams[cam].id.toUpperCase()} · {cams[cam].label} · Illustrative</span>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2" role="tablist" aria-label="Sample camera views">
          {cams.map((c, i) => (
            <button key={c.id} type="button" role="tab" aria-selected={i === cam} onClick={() => setCam(i)} data-testid={`brendan-cam-${c.id}`}
              className={`relative aspect-[4/3] overflow-hidden rounded-xl border transition-[border-color,opacity] duration-300 ${i === cam ? "border-orange-500" : "border-white/10 opacity-60 hover:opacity-100"}`}>
              <img src={c.src} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover grayscale" />
              <span className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/85 to-transparent" />
              <span className="absolute bottom-1 left-1.5 font-mono text-[9px] uppercase tracking-wider text-stone-100">{c.label}</span>
            </button>
          ))}
        </div>
        </div>

        <div className="flex min-w-0 flex-col gap-3 lg:col-span-2">
          <div className="rounded-2xl border border-white/10 bg-black/30 p-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-stone-400">Human-in-the-loop workflow</p>
            <ol className="mt-3 space-y-2">
              {stages.map((s, i) => {
                const Icon = s.icon;
                const on = i === step;
                return (
                  <li key={s.k} className={`flex items-start gap-3 rounded-xl px-3 py-2.5 transition-colors duration-500 ${on ? "bg-orange-500/15" : ""}`} data-testid={`brendan-stage-${s.k.toLowerCase()}`}>
                    <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${on ? "text-orange-400" : "text-stone-500"}`} />
                    <span>
                      <span className={`block text-sm font-medium ${on ? "text-stone-50" : "text-stone-300"}`}>{s.k}</span>
                      <span className="block text-xs leading-relaxed text-stone-400">{s.d}</span>
                    </span>
                  </li>
                );
              })}
            </ol>
          </div>
          <div className="rounded-2xl border border-white/10 bg-black/30 p-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-stone-400">Event log · example</p>
            <ul className="mt-3 space-y-2">
              {events.map(([t, e, s]) => (
                <li key={t + e} className="flex items-center justify-between gap-3 text-xs">
                  <span className="font-mono text-stone-400">{t}</span>
                  <span className="flex-1 truncate text-stone-300">{e}</span>
                  <span className={`rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase ${tone[s]}`}>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <div className="mt-3 grid gap-3 lg:grid-cols-5">
        <div className="rounded-2xl border border-white/10 bg-black/30 p-4 lg:col-span-3" data-testid="brendan-timeline">
          <div className="flex items-center justify-between">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-stone-400">Incident timeline · sample night</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-amber-300/80">Simulated · hover markers</p>
          </div>
          <div className="relative mt-8 h-14">
            <div className="absolute inset-x-0 top-1/2 h-px bg-white/15" />
            {!reduce && <span className="tc-playhead absolute top-0 h-full w-px bg-gradient-to-b from-transparent via-orange-400 to-transparent" />}
            {marks.map((m) => (
              <span key={m.t} tabIndex={0} aria-label={`${m.t} ${m.l}`} className="group/m absolute top-1/2 -translate-x-1/2 -translate-y-1/2 outline-none" style={{ left: `${m.x}%` }}>
                <span className={`block h-3 w-3 rotate-45 border transition-transform duration-300 group-hover/m:scale-150 group-focus/m:scale-150 ${tone[m.s]}`} />
                <span className="pointer-events-none absolute bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-black/90 px-2 py-1 text-[10px] text-stone-200 opacity-0 transition-opacity duration-300 group-hover/m:opacity-100 group-focus/m:opacity-100">{m.t} · {m.l}</span>
              </span>
            ))}
          </div>
          <div className="mt-2 flex justify-between font-mono text-[10px] text-stone-400">
            {["18:00", "21:00", "00:00", "03:00", "06:00"].map((h) => <span key={h}>{h}</span>)}
          </div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-black/30 p-4 lg:col-span-2" data-testid="brendan-signal">
          <div className="flex items-center justify-between">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-stone-400">AI activity signal</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-amber-300/80">Simulated</p>
          </div>
          <div className="mt-5 flex h-16 items-end gap-[3px]" aria-hidden="true">
            {signal.map((v, i) => (
              <span key={i} className={`flex-1 rounded-sm ${v > 70 ? "bg-orange-400" : "bg-white/15"}`} style={{ height: `${v}%` }} />
            ))}
          </div>
          <p className="mt-3 text-xs leading-relaxed text-stone-400">Peaks illustrate moments the system would surface for human review — never automatic action.</p>
        </div>
      </div>
      <p className="mt-3 flex items-start gap-2 px-2 pb-1 text-xs leading-relaxed text-stone-400">
        <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-400" />
        Illustration only. Brendan is in development and does not currently provide live monitoring, emergency response or guaranteed threat detection.
      </p>
    </div>
  );
};
